import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Phone, Mail, Car, Wifi, Coffee, ChevronRight, X, ChevronLeft, Clock, Users, Send, ExternalLink, Sparkles, Star } from 'lucide-react';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import BookingDatePicker from '../components/BookingDatePicker';
import { SiteHeader, SiteFooter, FooterCTA, MobileBookBar } from '../components/SiteLayout';
import FeaturedLPStrip from '../components/FeaturedLPStrip';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DIRECT_BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

const buildBookingUrl = (opts = {}) => {
  const p = new URLSearchParams();
  p.set('locale', 'en');
  p.set('items[0][adults]', String(opts.adults || 2));
  p.set('items[0][children]', '0');
  p.set('items[0][infants]', '0');
  p.set('currency', 'GBP');
  if (opts.checkIn) p.set('checkInDate', opts.checkIn);
  if (opts.checkOut) p.set('checkOutDate', opts.checkOut);
  if (opts.promo) p.set('promotion_code', opts.promo);
  p.set('trackPage', 'no');
  return `${DIRECT_BOOKING_URL}?${p.toString()}`;
};

/* === Shared Components === */

function PageHero({ title, accent, subtitle, bgImage, children }) {
  return (
    <section className="relative min-h-[50vh] sm:min-h-[55vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1419] via-[#0F1419]/60 to-[#0F1419]/30" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-10 sm:pb-14 text-center">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#E8E4DC] mb-3" data-testid="section-page-title">
          {title} <span className="text-[#C9A961] italic">{accent}</span>
        </h1>
        {subtitle && <p className="text-base sm:text-lg text-[#E8E4DC]/70 max-w-2xl mx-auto">{subtitle}</p>}
        {children}
      </div>
    </section>
  );
}

function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  if (!images?.length) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white/70 hover:text-white z-10 cursor-pointer"><X className="w-6 h-6" /></button>
      <button onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + images.length) % images.length); }} className="absolute left-3 p-2 text-white/70 hover:text-white z-10 cursor-pointer"><ChevronLeft className="w-8 h-8" /></button>
      <img src={resolveImg(images[idx]?.url || images[idx])} alt={images[idx]?.alt_text || ''} className="max-h-[85vh] max-w-[90vw] object-contain" onClick={e => e.stopPropagation()} />
      <button onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % images.length); }} className="absolute right-3 p-2 text-white/70 hover:text-white z-10 cursor-pointer"><ChevronRight className="w-8 h-8" /></button>
      <div className="absolute bottom-4 text-white/50 text-xs">{idx + 1} / {images.length}</div>
    </div>
  );
}

function SectionDivider() {
  return <div className="w-24 sm:w-32 h-[2px] bg-gradient-to-r from-transparent via-[#C9A961] to-transparent mx-auto my-2" />;
}

/* === Main SectionPage === */

function SectionPage({ sectionKey, fallbackTitle }) {
  const [section, setSection] = useState(null);
  const [settings, setSettings] = useState({});
  const [rooms, setRooms] = useState([]);
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetches = [
      axios.get(`${API_URL}/content/website-sections/${sectionKey}`).then(r => r.data).catch(() => null),
      axios.get(`${API_URL}/content/settings`).then(r => r.data).catch(() => ({})),
    ];
    if (['rooms', 'book_direct', 'about'].includes(sectionKey)) fetches.push(axios.get(`${API_URL}/content/rooms`).then(r => r.data).catch(() => []));
    if (['rooms', 'book_direct'].includes(sectionKey)) fetches.push(axios.get(`${API_URL}/content/price-comparison`).then(r => r.data).catch(() => []));
    if (sectionKey === 'offers') fetches.push(axios.get(`${API_URL}/content/offers`).then(r => r.data).catch(() => []));
    if (sectionKey === 'events') fetches.push(axios.get(`${API_URL}/content/events`).then(r => r.data).catch(() => []));
    if (['gallery', 'about', 'rooms'].includes(sectionKey)) fetches.push(axios.get(`${API_URL}/content/gallery`).then(r => r.data).catch(() => []));

    Promise.all(fetches).then(results => {
      setSection(results[0]);
      setSettings(results[1]);
      let idx = 2;
      if (['rooms', 'book_direct', 'about'].includes(sectionKey)) { setRooms(results[idx] || []); idx++; }
      if (['rooms', 'book_direct'].includes(sectionKey)) { setPrices(results[idx] || []); idx++; }
      if (sectionKey === 'offers') { setOffers(results[idx] || []); idx++; }
      if (sectionKey === 'events') { setEvents(results[idx] || []); idx++; }
      if (['gallery', 'about', 'rooms'].includes(sectionKey)) { setGalleryImages(results[idx] || []); idx++; }
    }).finally(() => setLoading(false));
  }, [sectionKey]);

  const openBooking = useCallback((opts = {}) => {
    if (typeof opts === 'string') opts = { promo: opts };
    window.open(buildBookingUrl(opts), '_blank', 'noopener,noreferrer');
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0F1419] flex items-center justify-center"><div className="text-[#C9A961] font-serif text-lg">Loading...</div></div>;
  if (!section) return <div className="min-h-screen bg-[#0F1419] flex items-center justify-center"><div className="text-[#E8E4DC]">Page not found</div></div>;

  const sc = (field, fallback = '') => section.content?.[field] || fallback;
  const seoTitle = sc('seo_title') || `${fallbackTitle} | Wentworth House Hotel`;
  const seoDesc = sc('seo_description') || `${fallbackTitle} at Wentworth House Hotel, Bath.`;
  const routeMap = { about: '/about', rooms: '/rooms', gallery: '/gallery', offers: '/offers', events: '/events', contact: '/contact', discover_bath: '/discover-bath', amenities: '/amenities', book_direct: '/book-direct' };

  return (
    <div className="min-h-screen bg-[#0F1419] text-[#E8E4DC]">
      <SEOHead title={seoTitle} description={seoDesc} path={routeMap[sectionKey] || `/${sectionKey}`} settings={settings} />

      <SiteHeader />

      <main className="pt-[72px]">
        {sectionKey === 'about' && <AboutPage sc={sc} rooms={rooms} gallery={galleryImages} openBooking={openBooking} />}
        {sectionKey === 'rooms' && <RoomsPage sc={sc} rooms={rooms} gallery={galleryImages} openBooking={openBooking} prices={prices} />}
        {sectionKey === 'gallery' && <GalleryPage sc={sc} images={galleryImages} />}
        {sectionKey === 'offers' && <OffersPage sc={sc} offers={offers} openBooking={openBooking} />}
        {sectionKey === 'events' && <EventsPage sc={sc} events={events} openBooking={openBooking} />}
        {sectionKey === 'contact' && <ContactPage sc={sc} settings={settings} openBooking={openBooking} />}
        {sectionKey === 'discover_bath' && <DiscoverBathPage sc={sc} openBooking={openBooking} />}
        {sectionKey === 'amenities' && <AmenitiesPage sc={sc} openBooking={openBooking} />}
        {sectionKey === 'book_direct' && <BookDirectPage sc={sc} prices={prices} openBooking={openBooking} />}
      </main>

      <FooterCTA />
      <SiteFooter settings={settings} />
      <MobileBookBar />
    </div>
  );
}

/* =========== ABOUT PAGE =========== */
function AboutPage({ sc, rooms, gallery, openBooking }) {
  return (
    <>
      <PageHero title={sc('heading', 'Heritage')} accent={sc('heading_accent', 'Hospitality')} subtitle={sc('subtitle', 'A Georgian guest house in Bath, dating back to 1809')} bgImage={resolveImg(sc('hero_image')) || `${BACKEND_URL}/api/uploads/d6eb7e015bf94972.jpg`} />
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Our <span className="text-[#C9A961] italic">Story</span></h2><SectionDivider /></div>
          <div className="space-y-6 text-base sm:text-lg text-[#E8E4DC]/70 leading-relaxed">
            <p>{sc('paragraph1', 'Set within a beautiful Georgian building dating back to 1809, Wentworth House offers an elegant and comfortable stay in the historic city of Bath.')}</p>
            <p>{sc('paragraph2', 'Whether it\'s a romantic escape or a family getaway, experience warm hospitality, complimentary parking, and hearty English breakfasts in this UNESCO Heritage City.')}</p>
          </div>
        </div>
      </section>
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#1a1f2e]">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
          {(sc('stats') || [{value:'1809',label:'Building Established'},{value:'20+',label:'Years Hosting Guests'},{value:'15 min',label:'Walk to City Centre'},{value:'Free',label:'On-Site Parking'}]).map((s,i) => (
            <div key={i} className="text-center py-4"><div className="text-3xl sm:text-4xl font-serif text-[#C9A961] mb-1">{s.value}</div><div className="text-xs sm:text-sm text-[#E8E4DC]/50">{s.label}</div></div>
          ))}
        </div>
      </section>
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">What We <span className="text-[#C9A961] italic">Offer</span></h2><SectionDivider /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[{icon:Car,title:'Free Parking',desc:'Complimentary on-site parking for all guests.'},{icon:Coffee,title:'Full English Breakfast',desc:'Traditional cooked breakfast included with every booking.'},{icon:Wifi,title:'Free WiFi',desc:'High-speed WiFi throughout the hotel.'},{icon:MapPin,title:'Prime Location',desc:'15 minutes walk to Bath city centre and Roman Baths.'},{icon:Users,title:'Family & Couples',desc:'8 room types from cosy doubles to family apartments.'},{icon:Clock,title:'Flexible Check-in',desc:'Check-in from 3 PM. Late arrivals by arrangement.'}].map((item,i) => (
              <div key={i} className="border border-[#C9A961]/15 p-6 hover:border-[#C9A961]/40 transition-all"><item.icon className="w-6 h-6 text-[#C9A961] mb-3" strokeWidth={1.5} /><h3 className="font-serif text-[#E8E4DC] mb-2">{item.title}</h3><p className="text-sm text-[#E8E4DC]/50">{item.desc}</p></div>
            ))}
          </div>
        </div>
      </section>
      {gallery.length > 0 && <GalleryGrid images={gallery.slice(0, 8)} title="Our Hotel" />}
    </>
  );
}

function GalleryGrid({ images, title }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!images?.length) return null;
  return (
    <>
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {title && <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-8 text-center">{title}</h2>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {images.map((img, i) => (
              <div key={i} className="overflow-hidden border border-[#C9A961]/10 hover:border-[#C9A961]/40 transition-all cursor-pointer group" onClick={() => setLightboxIdx(i)}>
                <img src={resolveImg(img.url || img)} alt={img.alt_text || ''} className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>
      {lightboxIdx !== null && <Lightbox images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />}
    </>
  );
}

/* =========== ROOMS PAGE =========== */
function RoomsPage({ sc, rooms, gallery, openBooking, prices }) {
  const [lightboxRoom, setLightboxRoom] = useState(null);
  const priceMap = {};
  (prices || []).forEach(p => { if (!priceMap[p.room || p.room_name] || (p.direct_price < (priceMap[p.room || p.room_name].direct_price))) priceMap[p.room || p.room_name] = p; });

  // Get the check date from price data
  const priceCheckDate = prices.length > 0 ? prices[0].date : null;

  return (
    <>
      <PageHero title={sc('heading', 'Our')} accent={sc('heading_accent', 'Rooms & Suites')} subtitle={sc('subtitle', '8 room types to suit every guest — from cosy doubles to family apartments')} bgImage={resolveImg(sc('hero_image')) || 'https://images.pexels.com/photos/18201945/pexels-photo-18201945.jpeg?auto=compress&cs=tinysrgb&w=1200'}>
        <div className="mt-6 max-w-3xl mx-auto"><BookingDatePicker /></div>
      </PageHero>

      {/* Featured live landing pages */}
      <FeaturedLPStrip heading="Featured Campaigns" subheading="Targeted offers you might like — book direct for best rates" />

      {sc('intro_text') && (
        <section className="pt-12 sm:pt-16 px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base sm:text-lg text-[#E8E4DC]/70 leading-relaxed whitespace-pre-line">{sc('intro_text')}</p>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto space-y-8">
          {rooms.map((room, i) => {
            const livePrice = priceMap[room.name];
            const roomImages = (room.images && room.images.length > 0) ? room.images : (room.image ? [room.image] : []);
            return (
              <div key={i} className="border border-[#C9A961]/20 hover:border-[#C9A961]/40 transition-all overflow-hidden" data-testid={`room-detail-${i}`}>
                <div className="flex flex-col lg:flex-row">
                  {roomImages.length > 0 && (
                    <div className="lg:w-2/5 h-56 lg:h-auto overflow-hidden relative group cursor-pointer" onClick={() => roomImages.length > 1 && setLightboxRoom({ images: roomImages.map(u => ({ url: u })), idx: 0 })}>
                      <img src={resolveImg(roomImages[0])} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      {roomImages.length > 1 && <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded">{roomImages.length} photos</div>}
                    </div>
                  )}
                  <div className="flex-1 p-6 sm:p-8">
                    <div className="flex items-start justify-between mb-3">
                      <h2 className="text-xl sm:text-2xl font-serif text-[#E8E4DC]">{room.name}</h2>
                      <div className="text-right ml-4 shrink-0">
                        <div className="text-[10px] text-[#C9A961]/60 uppercase tracking-wider">From</div>
                        <div className="text-xl sm:text-2xl font-serif text-[#C9A961]">£{livePrice?.direct_price || room.price}</div>
                        <div className="text-[10px] text-[#E8E4DC]/40">per night</div>
                      </div>
                    </div>
                    <p className="text-sm text-[#E8E4DC]/60 mb-4">{room.description}</p>
                    {/* Price comparison — subtle but convincing */}
                    {livePrice && livePrice.saving > 0 && (
                      <div className="mb-4 bg-[#C9A961]/5 border border-[#C9A961]/10 p-2.5 rounded">
                        <div className="flex items-center justify-between text-xs">
                          <div>
                            <span className="text-[#E8E4DC]/40">Booking.com: </span>
                            <span className="text-[#E8E4DC]/35 line-through">£{(livePrice.booking_com_price || livePrice.competitor_price || 0).toFixed(0)}</span>
                          </div>
                          <div className="bg-green-900/40 text-green-400 border border-green-800/30 px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase">
                            Save £{livePrice.saving.toFixed(0)} Direct
                          </div>
                        </div>
                        {priceCheckDate && <p className="text-[9px] text-[#E8E4DC]/25 mt-1.5">Prices checked for {new Date(priceCheckDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-[10px] border border-[#C9A961]/20 text-[#C9A961]/70 px-2.5 py-1">Sleeps {room.max_occupancy || 2}</span>
                      {(room.amenities || []).slice(0, 5).map((a, j) => <span key={j} className="text-[10px] border border-[#C9A961]/10 text-[#E8E4DC]/40 px-2.5 py-1">{a}</span>)}
                    </div>
                    <a href={buildBookingUrl()} target="_blank" rel="noopener noreferrer external" className="inline-block bg-[#C9A961] text-[#0F1419] px-6 py-2.5 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E] transition-all">Check Availability</a>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="text-center space-y-2 mt-6">
            <p className="text-[10px] text-[#E8E4DC]/25">Prices shown are estimates and may vary depending on dates, season, demand, and availability. Rates are not guaranteed until booking is confirmed. Best rates are available when booking direct.</p>
            {priceCheckDate && <p className="text-[9px] text-[#E8E4DC]/20">Last price comparison: {new Date(priceCheckDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
          </div>
        </div>
      </section>

      {lightboxRoom && <Lightbox images={lightboxRoom.images} startIndex={lightboxRoom.idx} onClose={() => setLightboxRoom(null)} />}
    </>
  );
}

/* =========== OFFERS PAGE =========== */
function OffersPage({ sc, offers, openBooking }) {
  return (
    <>
      <PageHero title={sc('heading', 'Exclusive')} accent={sc('heading_accent', 'Offers')} subtitle={sc('subtitle', 'Book direct for the best rates — guaranteed')} bgImage={resolveImg(sc('hero_image')) || 'https://images.unsplash.com/photo-1730832445769-d5a52ae0d0e2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200'}>
        <div className="mt-6 max-w-3xl mx-auto"><BookingDatePicker /></div>
      </PageHero>

      {/* Urgency banner */}
      <div className="bg-[#C9A961]/10 border-y border-[#C9A961]/20 py-3 text-center">
        <p className="text-xs sm:text-sm text-[#C9A961]"><Sparkles className="w-4 h-4 inline mr-1" /> Book early to secure the best rates — limited availability during peak season</p>
      </div>

      {/* Featured live landing pages */}
      <FeaturedLPStrip heading="Live Campaigns" subheading="Current direct-book offers and seasonal promotions" />

      {sc('intro_text') && (
        <section className="pt-10 sm:pt-14 px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base sm:text-lg text-[#E8E4DC]/70 leading-relaxed whitespace-pre-line">{sc('intro_text')}</p>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          {offers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {offers.map((offer, i) => (
                <div key={i} className="border border-[#C9A961]/20 bg-gradient-to-br from-[#1a1f2e] to-[#0F1419] overflow-hidden hover:border-[#C9A961]/50 transition-all flex flex-col" data-testid={`offer-card-${i}`}>
                  {offer.image && <div className="h-44 overflow-hidden"><img src={resolveImg(offer.image)} alt={offer.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" /></div>}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-lg font-serif text-[#E8E4DC] mb-2">{offer.title}</h3>
                    {offer.discount && <div className="text-[#C9A961] font-serif text-2xl mb-3">{offer.discount}</div>}
                    <p className="text-sm text-[#E8E4DC]/50 mb-4 flex-1">{offer.description}</p>
                    {offer.valid_until && <p className="text-[10px] text-[#C9A961]/50 mb-4">Available until {offer.valid_until} — book now to avoid missing out</p>}
                    <a href={buildBookingUrl({ promo: offer.discount_code })} target="_blank" rel="noopener noreferrer external" className="w-full bg-[#C9A961] text-[#0F1419] py-3 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E] transition-all text-center block">
                      {offer.discount_code ? `Book with code: ${offer.discount_code}` : 'Book This Offer'}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h2 className="text-xl font-serif text-[#E8E4DC]/60 mb-4">No active offers at the moment</h2>
              <p className="text-sm text-[#E8E4DC]/40 mb-8">Check back soon or book direct for the best available rates.</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#1a1f2e]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-serif text-[#E8E4DC] mb-6">Why <span className="text-[#C9A961] italic">Book Direct?</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[{title:'Best Price Guarantee',desc:'We match or beat any rate you find online.'},{title:'Free Cancellation',desc:'Flexible cancellation on selected rates.'},{title:'Exclusive Perks',desc:'Promo codes and seasonal extras for direct guests.'}].map((b,i) => (
              <div key={i} className="border border-[#C9A961]/15 p-5"><h3 className="font-serif text-[#C9A961] mb-2">{b.title}</h3><p className="text-sm text-[#E8E4DC]/50">{b.desc}</p></div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* =========== EVENTS PAGE =========== */
function EventsPage({ sc, events, openBooking }) {
  const getEventBookingUrl = (event) => {
    if (!event.date) return buildBookingUrl();
    const startDate = event.date;
    const endDate = event.end_date || event.date;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const checkIn = startDate;
    let checkOut;
    if (diffDays > 7) {
      const co = new Date(start); co.setDate(co.getDate() + 2);
      checkOut = co.toISOString().split('T')[0];
    } else {
      const co = new Date(start); co.setDate(co.getDate() + 1);
      checkOut = co.toISOString().split('T')[0];
    }
    return buildBookingUrl({ checkIn, checkOut });
  };

  return (
    <>
      <PageHero title={sc('heading', 'Events in')} accent={sc('heading_accent', 'Bath')} subtitle={sc('subtitle', 'Plan your visit around Bath\'s exciting events calendar')} bgImage={resolveImg(sc('hero_image')) || 'https://images.pexels.com/photos/18222697/pexels-photo-18222697.jpeg?auto=compress&cs=tinysrgb&w=1200'}>
        <div className="mt-6 max-w-3xl mx-auto"><BookingDatePicker /></div>
      </PageHero>

      {sc('intro_text') && (
        <section className="pt-12 sm:pt-16 px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base sm:text-lg text-[#E8E4DC]/70 leading-relaxed whitespace-pre-line">{sc('intro_text')}</p>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          {events.length > 0 ? (
            <div className="space-y-6">
              {events.map((event, i) => (
                <div key={i} className="border border-[#C9A961]/20 hover:border-[#C9A961]/40 transition-all overflow-hidden flex flex-col sm:flex-row" data-testid={`event-card-${i}`}>
                  {(event.image_thumb || event.image) && <div className="sm:w-56 h-44 sm:h-auto overflow-hidden shrink-0"><img src={resolveImg(event.image_thumb || event.image)} alt={event.title} className="w-full h-full object-cover object-center" loading="lazy" /></div>}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-2">
                      {event.slug ? (
                        <Link to={`/events/${event.slug}`} className="text-lg font-serif text-[#E8E4DC] hover:text-[#C9A961] transition-colors" data-testid={`event-link-${event.slug}`}>{event.title}</Link>
                      ) : (
                        <h3 className="text-lg font-serif text-[#E8E4DC]">{event.title}</h3>
                      )}
                      {event.date && <div className="text-right ml-4 shrink-0 bg-[#C9A961]/10 border border-[#C9A961]/20 px-3 py-1.5"><div className="text-xs text-[#C9A961] font-semibold">{event.date}</div>{event.end_date && event.end_date !== event.date && <div className="text-[10px] text-[#C9A961]/60">to {event.end_date}</div>}</div>}
                    </div>
                    {event.location && <p className="text-xs text-[#C9A961]/60 mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</p>}
                    <p className="text-sm text-[#E8E4DC]/60 mb-4 flex-1">{event.description}</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {event.slug && (
                        <Link to={`/events/${event.slug}`} className="bg-[#C9A961] text-[#0F1419] px-5 py-2 text-xs tracking-[0.1em] uppercase font-bold hover:bg-[#D4B76E] transition-all" data-testid={`event-more-details-${event.slug}`}>More Details</Link>
                      )}
                      <a href={getEventBookingUrl(event)} target="_blank" rel="noopener noreferrer external" className="bg-[#C9A961]/10 border border-[#C9A961]/30 text-[#C9A961] px-5 py-2 text-xs tracking-[0.1em] uppercase font-bold hover:bg-[#C9A961] hover:text-[#0F1419] transition-all">Book for This Event</a>
                      {event.external_link && <a href={event.external_link} target="_blank" rel="noopener noreferrer nofollow external" className="text-xs text-[#C9A961]/70 hover:text-[#C9A961] underline transition-colors">Official site &rarr;</a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16"><h2 className="text-xl font-serif text-[#E8E4DC]/60 mb-4">Events coming soon</h2><p className="text-sm text-[#E8E4DC]/40 mb-8">We're updating our events calendar.</p></div>
          )}
        </div>
      </section>
    </>
  );
}

/* =========== CONTACT PAGE =========== */
function ContactPage({ sc, settings, openBooking }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', honeypot: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    setSending(true); setError('');
    try {
      await axios.post(`${API_URL}/contact/submit`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSending(false); }
  };

  return (
    <>
      <PageHero title={sc('heading', 'Get in')} accent={sc('heading_accent', 'Touch')} subtitle={sc('subtitle', 'We\'d love to hear from you. Reach out with any questions about your stay.')} bgImage={resolveImg(sc('hero_image')) || `${BACKEND_URL}/api/uploads/d6eb7e015bf94972.jpg`} />

      {/* Contact Cards */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-10 bg-[#1a1f2e]">
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: MapPin, label: 'Address', val: '106 Bloomfield Road, Bath, BA2 2AP' },
            { icon: Phone, label: 'Phone', val: settings.phone || '01225 339193', href: `tel:${settings.phone || '01225339193'}` },
            { icon: Mail, label: 'Email', val: settings.email || 'stay@wentworthhouse.co.uk', href: `mailto:${settings.email || 'stay@wentworthhouse.co.uk'}` },
            { icon: Clock, label: 'Reception', val: 'Check-in 3 PM - 10 PM' },
          ].map((c, i) => (
            <div key={i} className="border border-[#C9A961]/15 p-5 text-center hover:border-[#C9A961]/40 transition-all">
              <c.icon className="w-6 h-6 text-[#C9A961] mx-auto mb-3" strokeWidth={1.5} />
              <div className="text-[10px] text-[#C9A961]/60 uppercase tracking-wider mb-1">{c.label}</div>
              {c.href ? (
                <a href={c.href} className="text-sm text-[#E8E4DC]/70 hover:text-[#C9A961] transition-colors break-words">{c.val}</a>
              ) : (
                <p className="text-sm text-[#E8E4DC]/70">{c.val}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Form */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Send Us a <span className="text-[#C9A961] italic">Message</span></h2>
              <p className="text-sm text-[#E8E4DC]/50 mb-8">Have a question or special request? We'll get back to you as soon as possible.</p>
              {sent ? (
                <div className="border border-green-800/30 bg-green-900/10 p-10 text-center" data-testid="contact-form-success">
                  <div className="text-green-400 text-xl font-serif mb-3">Thank You!</div>
                  <p className="text-sm text-[#E8E4DC]/60">Your enquiry has been received. We'll respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <input type="text" name="website" value={form.honeypot} onChange={e => setForm({...form, honeypot: e.target.value})} className="hidden" tabIndex="-1" autoComplete="off" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-[#C9A961]/60 uppercase tracking-wider mb-2">Full Name *</label>
                      <input type="text" required minLength={2} value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full bg-transparent border-b border-[#C9A961]/20 focus:border-[#C9A961] px-1 py-3 text-sm text-[#E8E4DC] outline-none transition-colors placeholder-[#E8E4DC]/20" placeholder="Your name" data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#C9A961]/60 uppercase tracking-wider mb-2">Email *</label>
                      <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-transparent border-b border-[#C9A961]/20 focus:border-[#C9A961] px-1 py-3 text-sm text-[#E8E4DC] outline-none transition-colors placeholder-[#E8E4DC]/20" placeholder="your@email.com" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-[#C9A961]/60 uppercase tracking-wider mb-2">Phone</label>
                      <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full bg-transparent border-b border-[#C9A961]/20 focus:border-[#C9A961] px-1 py-3 text-sm text-[#E8E4DC] outline-none transition-colors placeholder-[#E8E4DC]/20" placeholder="Optional" data-testid="contact-phone" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#C9A961]/60 uppercase tracking-wider mb-2">Subject *</label>
                      <input type="text" required minLength={3} value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full bg-transparent border-b border-[#C9A961]/20 focus:border-[#C9A961] px-1 py-3 text-sm text-[#E8E4DC] outline-none transition-colors placeholder-[#E8E4DC]/20" placeholder="What's this about?" data-testid="contact-subject" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[#C9A961]/60 uppercase tracking-wider mb-2">Message *</label>
                    <textarea required minLength={10} rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full bg-transparent border border-[#C9A961]/20 focus:border-[#C9A961] px-4 py-3 text-sm text-[#E8E4DC] outline-none transition-colors resize-none placeholder-[#E8E4DC]/20" placeholder="Tell us how we can help..." data-testid="contact-message" />
                  </div>
                  {error && <p className="text-red-400 text-xs">{error}</p>}
                  <button type="submit" disabled={sending} className="bg-[#C9A961] text-[#0F1419] px-10 py-3.5 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E] transition-all disabled:opacity-50 inline-flex items-center gap-2 cursor-pointer" data-testid="contact-submit">
                    <Send className="w-4 h-4" />{sending ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </form>
              )}
            </div>

            {/* Map */}
            <div className="lg:col-span-2">
              <div className="border border-[#C9A961]/20 overflow-hidden h-full min-h-[400px] lg:min-h-[500px]">
                <iframe src={sc('map_embed_url','https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2487.0523684739586!2d-2.3644417!3d51.3823449!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48718174b3b9a2cd%3A0xb64ea75c5e44b8e7!2sWentworth%20House%20Hotel!5e0!3m2!1sen!2suk!4v1711500000000!5m2!1sen!2suk')} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Wentworth House Hotel Location"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#1a1f2e]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8"><h2 className="text-xl sm:text-2xl font-serif text-[#E8E4DC] mb-2">Getting <span className="text-[#C9A961] italic">Here</span></h2><SectionDivider /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[{icon:Car,title:'By Car',text:'Free on-site parking available. From the M4, take junction 18 towards Bath.'},{icon:Clock,title:'By Train',text:'Bath Spa station is a 10-minute taxi ride. Direct trains from London Paddington (90 mins).'},{icon:MapPin,title:'On Foot',text:'15-minute walk to city centre, Roman Baths, and Bath Abbey via Beechen Cliff.'}].map((item,i) => (
              <div key={i} className="border border-[#C9A961]/15 p-5 text-center hover:border-[#C9A961]/40 transition-all">
                <item.icon className="w-6 h-6 text-[#C9A961] mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="font-serif text-[#C9A961] mb-2">{item.title}</h3>
                <p className="text-sm text-[#E8E4DC]/50">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* =========== DISCOVER BATH PAGE =========== */
function DiscoverBathPage({ sc, openBooking }) {
  const BATH_IMAGES = {
    pulteney: 'https://images.pexels.com/photos/18222697/pexels-photo-18222697.jpeg?auto=compress&cs=tinysrgb&w=1200',
    pulteney2: 'https://images.pexels.com/photos/31147892/pexels-photo-31147892.jpeg?auto=compress&cs=tinysrgb&w=800',
    spa: 'https://images.pexels.com/photos/4353638/pexels-photo-4353638.jpeg?auto=compress&cs=tinysrgb&w=800',
    city: 'https://images.unsplash.com/photo-1730832446575-a10586448cb0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    arch: 'https://images.pexels.com/photos/6302960/pexels-photo-6302960.jpeg?auto=compress&cs=tinysrgb&w=800',
  };

  // Reorderable content blocks keyed by subsection key (matches admin editor)
  const blockRenderers = {
    why_stay: (
      <section key="why_stay" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Why Stay <span className="text-[#C9A961] italic">With Us?</span></h2><SectionDivider /></div>
          <p className="text-base sm:text-lg text-[#E8E4DC]/70 leading-relaxed text-center mb-10 max-w-3xl mx-auto">Wentworth House Hotel offers the perfect base for exploring Bath. Unlike many city centre hotels, we provide free on-site parking, a quiet and relaxed environment, and easy access to the main attractions.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[{icon:Car,title:'Free Parking',desc:'Save up to £20/day with complimentary on-site parking.'},{icon:MapPin,title:'Quiet Location',desc:'Peaceful Bloomfield Road, away from the bustle.'},{icon:Coffee,title:'Breakfast Included',desc:'Full English breakfast every morning.'},{icon:Users,title:'For Everyone',desc:'Couples, families, solo & business guests.'}].map((item,i) => (
              <div key={i} className="border border-[#C9A961]/15 p-5 hover:border-[#C9A961]/40 transition-all text-center">
                <item.icon className="w-7 h-7 text-[#C9A961] mb-3 mx-auto" strokeWidth={1.5} />
                <h3 className="font-serif text-[#E8E4DC] mb-1 text-sm">{item.title}</h3>
                <p className="text-xs text-[#E8E4DC]/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    attractions: (
      <section key="attractions" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10 bg-[#1a1f2e]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Historic <span className="text-[#C9A961] italic">Attractions</span></h2><SectionDivider /><p className="text-sm text-[#E8E4DC]/50 mt-4">All within easy reach of the hotel</p></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {name:'Roman Baths',dist:'5-7 mins drive',desc:'One of the best-preserved Roman sites in the world. Walk in the footsteps of history.',url:'https://www.romanbaths.co.uk/',img:BATH_IMAGES.arch},
              {name:'Bath Abbey',dist:'5-7 mins drive',desc:'A stunning medieval church in the heart of the city with breathtaking fan-vaulted ceiling.',url:'https://www.bathabbey.org/',img:BATH_IMAGES.pulteney2},
              {name:'Royal Crescent',dist:'8-10 mins drive',desc:'Bath\'s most iconic landmark — 30 terraced houses in a sweeping crescent, Grade I listed.',url:'https://www.nationaltrust.org.uk/visit/bath-bristol/royal-crescent',img:BATH_IMAGES.city},
              {name:'The Circus',dist:'8-10 mins drive',desc:'A circle of 33 townhouses designed by John Wood the Elder. Architectural masterpiece.'},
              {name:'Pulteney Bridge',dist:'5-7 mins drive',desc:'One of only four bridges in the world with shops on both sides. Iconic riverside views.',img:BATH_IMAGES.pulteney},
            ].map((a,i) => (
              <div key={i} className="border border-[#C9A961]/15 overflow-hidden hover:border-[#C9A961]/40 transition-all group">
                {a.img && <div className="h-40 overflow-hidden"><img src={a.img} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>}
                <div className="p-5">
                  <h3 className="font-serif text-[#E8E4DC] mb-1">{a.name}</h3>
                  <p className="text-[10px] text-[#C9A961]/60 mb-2 uppercase tracking-wider">{a.dist}</p>
                  <p className="text-sm text-[#E8E4DC]/50 mb-3">{a.desc}</p>
                  {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer external" className="text-xs text-[#C9A961] hover:underline inline-flex items-center gap-1">Visit Website <ExternalLink className="w-3 h-3" /></a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    spa: (
      <section key="spa" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mx-auto">
            <div className="border border-[#C9A961]/15 overflow-hidden hover:border-[#C9A961]/40 transition-all">
              <div className="h-48 overflow-hidden"><img src={BATH_IMAGES.spa} alt="Thermae Bath Spa" className="w-full h-full object-cover" loading="lazy" /></div>
              <div className="p-6">
                <h2 className="text-xl font-serif text-[#E8E4DC] mb-2">Spa & <span className="text-[#C9A961] italic">Relaxation</span></h2>
                <h3 className="font-serif text-[#C9A961] mb-2">Thermae Bath Spa</h3>
                <p className="text-sm text-[#E8E4DC]/50 mb-3">Britain's only natural thermal spa — rooftop pool with stunning views over Bath. The perfect way to end a day of exploring.</p>
                <a href="https://www.thermaebathspa.com/" target="_blank" rel="noopener noreferrer external" className="text-xs text-[#C9A961] hover:underline inline-flex items-center gap-1">Visit Website <ExternalLink className="w-3 h-3" /></a>
              </div>
            </div>
          </div>
        </div>
      </section>
    ),
    culture: (
      <section key="culture" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10 bg-[#1a1f2e]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Culture & <span className="text-[#C9A961] italic">Experiences</span></h2><SectionDivider /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[{name:'Jane Austen Centre',desc:'Step into Regency Bath and discover Austen\'s life here.',url:'https://janeausten.co.uk/'},{name:'Holburne Museum',desc:'Fine and decorative arts in a beautiful Georgian building.'},{name:'Theatre Royal Bath',desc:'One of the oldest and most prestigious theatres in England.'}].map((c,i) => (
              <div key={i} className="border border-[#C9A961]/15 p-5 hover:border-[#C9A961]/40 transition-all">
                <h3 className="font-serif text-[#E8E4DC] mb-1">{c.name}</h3>
                <p className="text-sm text-[#E8E4DC]/50 mb-2">{c.desc}</p>
                {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer external" className="text-[10px] text-[#C9A961] hover:underline inline-flex items-center gap-1">Visit Website <ExternalLink className="w-3 h-3" /></a>}
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    shopping: (
      <section key="shopping" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2"><span className="text-[#C9A961] italic">Shopping</span></h2><SectionDivider /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[{name:'Milsom Street',desc:'Designer boutiques and high-end shops in Bath\'s premier shopping street.'},{name:'Walcot Street',desc:'Eclectic independent shops, vintage finds, and artisan markets.'}].map((s,i) => (
              <div key={i} className="border border-[#C9A961]/15 p-5 hover:border-[#C9A961]/40 transition-all"><h3 className="font-serif text-[#E8E4DC] text-sm mb-1">{s.name}</h3><p className="text-xs text-[#E8E4DC]/50">{s.desc}</p></div>
            ))}
          </div>
        </div>
      </section>
    ),
    food: (
      <section key="food" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10 bg-[#1a1f2e]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Food & <span className="text-[#C9A961] italic">Drink</span></h2><SectionDivider /></div>
          <div className="border border-[#C9A961]/15 p-6 hover:border-[#C9A961]/40 transition-all max-w-xl mx-auto">
            <h3 className="font-serif text-[#E8E4DC] mb-1">Sally Lunn's</h3>
            <p className="text-sm text-[#E8E4DC]/50">Famous for the original Bath Bun — housed in one of the oldest buildings in the city (1482). A must-visit for any foodie.</p>
          </div>
        </div>
      </section>
    ),
    annual_events: (
      <section key="annual_events" className="py-14 sm:py-18 px-4 sm:px-6 bg-[#1a1f2e]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Annual <span className="text-[#C9A961] italic">Events</span></h2><SectionDivider /><p className="text-sm text-[#E8E4DC]/50 mt-4">Plan your visit around these popular Bath events</p></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[{name:'Bath Christmas Market',desc:'One of the UK\'s most enchanting Christmas markets. Over 170 stalls in a stunning Georgian setting.',season:'Nov - Dec'},{name:'Bath Festival',desc:'A world-class celebration of music, literature, and ideas in the heart of the city.',season:'May'},{name:'Jane Austen Festival',desc:'The largest Jane Austen festival in the world — Regency-era promenades, talks, and balls.',season:'September'}].map((e,i) => (
              <div key={i} className="border border-[#C9A961]/20 p-5 hover:border-[#C9A961]/40 transition-all bg-[#0F1419]/50">
                <div className="text-[10px] text-[#C9A961] uppercase tracking-wider mb-2">{e.season}</div>
                <h3 className="font-serif text-[#E8E4DC] mb-2">{e.name}</h3>
                <p className="text-xs text-[#E8E4DC]/50">{e.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8"><Link to="/events" className="inline-flex items-center gap-2 text-[#C9A961] text-sm hover:underline transition-colors">View all upcoming events <ChevronRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    ),
    itineraries: (
      <section key="itineraries" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10"><h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-2">Suggested <span className="text-[#C9A961] italic">Itineraries</span></h2><SectionDivider /></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-[#C9A961]/20 p-6 sm:p-8 hover:border-[#C9A961]/40 transition-all">
              <div className="text-[10px] text-[#C9A961] uppercase tracking-wider mb-2">Perfect day out</div>
              <h3 className="font-serif text-[#C9A961] text-xl mb-4">1-Day Bath Itinerary</h3>
              <div className="space-y-4">
                {[{time:'Morning',text:'Full English breakfast at the hotel, then walk to the Roman Baths'},{time:'Midday',text:'Bath Abbey & Pulteney Bridge, lunch at Sally Lunn\'s'},{time:'Afternoon',text:'Royal Crescent & The Circus, shopping on Milsom Street'},{time:'Evening',text:'Thermae Bath Spa rooftop pool at sunset'}].map((step,i) => (
                  <div key={i} className="flex gap-3"><div className="w-16 shrink-0 text-xs text-[#C9A961] font-semibold pt-0.5">{step.time}</div><p className="text-sm text-[#E8E4DC]/60">{step.text}</p></div>
                ))}
              </div>
            </div>
            <div className="border border-[#C9A961]/20 p-6 sm:p-8 hover:border-[#C9A961]/40 transition-all">
              <div className="text-[10px] text-[#C9A961] uppercase tracking-wider mb-2">Extended stay</div>
              <h3 className="font-serif text-[#C9A961] text-xl mb-4">2-Day Relaxed Stay</h3>
              <div className="space-y-4">
                {[{time:'Day 1',text:'City centre exploration — Roman Baths, Abbey, Pulteney Bridge, SouthGate shops'},{time:'Day 1 Eve',text:'Theatre Royal show or fine dining in the city'},{time:'Day 2 AM',text:'Jane Austen Centre & Holburne Museum'},{time:'Day 2 PM',text:'Thermae Bath Spa full experience, Walcot Street independent shops'}].map((step,i) => (
                  <div key={i} className="flex gap-3"><div className="w-16 shrink-0 text-xs text-[#C9A961] font-semibold pt-0.5">{step.time}</div><p className="text-sm text-[#E8E4DC]/60">{step.text}</p></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    ),
  };

  // Default block order (matches legacy rendering)
  const defaultOrder = ['why_stay', 'attractions', 'spa', 'culture', 'shopping', 'food', 'annual_events', 'itineraries'];
  const savedOrder = sc('subsection_order');
  let orderedKeys = Array.isArray(savedOrder) && savedOrder.length > 0
    ? savedOrder.filter(k => blockRenderers[k])
    : defaultOrder;
  // Append any renderers not listed in savedOrder (e.g., new blocks added after save)
  defaultOrder.forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });

  return (
    <>
      <PageHero title={sc('heading', 'Discover Bath from')} accent={sc('heading_accent', 'Wentworth House Hotel')} subtitle={sc('subtitle', 'Experience the best of Bath while staying in a peaceful, well-connected location.')} bgImage={resolveImg(sc('hero_image')) || 'https://images.pexels.com/photos/18222697/pexels-photo-18222697.jpeg?auto=compress&cs=tinysrgb&w=1200'}>
        <div className="mt-6 max-w-3xl mx-auto"><BookingDatePicker /></div>
      </PageHero>

      {orderedKeys.map(k => blockRenderers[k])}

      {/* Map — fixed at bottom */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#1a1f2e]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6"><h2 className="text-xl font-serif text-[#E8E4DC] mb-2">Our <span className="text-[#C9A961] italic">Location</span></h2><SectionDivider /></div>
          <div className="border border-[#C9A961]/20 overflow-hidden h-80 sm:h-96">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2487.0523684739586!2d-2.3644417!3d51.3823449!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48718174b3b9a2cd%3A0xb64ea75c5e44b8e7!2sWentworth%20House%20Hotel!5e0!3m2!1sen!2suk!4v1711500000000!5m2!1sen!2suk" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Location"></iframe>
          </div>
        </div>
      </section>
    </>
  );
}

/* =========== GALLERY / AMENITIES / BOOK DIRECT =========== */
function GalleryPage({ sc, images }) {
  return (
    <>
      <PageHero title={sc('heading','Our')} accent={sc('heading_accent','Gallery')} subtitle={sc('subtitle','A closer look at Wentworth House Hotel')} bgImage={resolveImg(images[0]?.url) || `${BACKEND_URL}/api/uploads/d6eb7e015bf94972.jpg`} />
      <GalleryGrid images={images} />
    </>
  );
}

function AmenitiesPage({ sc, openBooking }) {
  const items = sc('items') || [];
  return (
    <>
      <PageHero title={sc('heading','Hotel')} accent={sc('heading_accent','Amenities')} subtitle="Everything you need for a comfortable stay" bgImage={resolveImg(sc('hero_image')) || `${BACKEND_URL}/api/uploads/d6eb7e015bf94972.jpg`} />
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item,i) => (<div key={i} className="border border-[#C9A961]/15 p-6 hover:border-[#C9A961]/40 transition-all"><div className="text-[#C9A961] font-serif text-lg mb-2">{item.label}</div>{item.description && <p className="text-sm text-[#E8E4DC]/50">{item.description}</p>}</div>))}
        </div>
      </section>
    </>
  );
}

function BookDirectPage({ sc, prices, openBooking }) {
  const roomBest = {};
  (prices || []).forEach(p => { if (!roomBest[p.room_name] || p.saving > roomBest[p.room_name].saving) roomBest[p.room_name] = p; });
  const bestPrices = Object.values(roomBest);
  return (
    <>
      <PageHero title={sc('heading','Book Direct')} accent={sc('heading_accent','& Save')} subtitle="Best rates guaranteed when you book directly" bgImage={resolveImg(sc('hero_image')) || `${BACKEND_URL}/api/uploads/d6eb7e015bf94972.jpg`} />
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {bestPrices.map((p,i) => (
            <div key={i} className="border border-[#C9A961]/20 p-5 hover:border-[#C9A961]/50 transition-all">
              <h3 className="text-sm font-serif text-[#E8E4DC] mb-3">{p.room_name}</h3>
              <div className="flex items-baseline gap-2 mb-1"><span className="text-xl font-serif text-[#C9A961]">£{p.direct_price}</span><span className="text-xs text-[#E8E4DC]/40 line-through">£{p.competitor_price}</span></div>
              {p.saving > 0 && <p className="text-xs text-green-400 mb-4">Save £{p.saving.toFixed(0)}</p>}
              <a href={buildBookingUrl()} target="_blank" rel="noopener noreferrer external" className="w-full bg-[#C9A961] text-[#0F1419] py-2.5 text-xs tracking-[0.1em] uppercase font-bold hover:bg-[#D4B76E] transition-all text-center block">Book Direct</a>
            </div>
          ))}
        </div>
        {bestPrices.length === 0 && <div className="text-center py-12"><p className="text-[#E8E4DC]/50 mb-6">Price comparisons will appear after the next rate check.</p></div>}
      </section>
    </>
  );
}

export default SectionPage;
